import { useEffect, useState } from 'react';
import { deleteData, getData, postData, putData } from '../../../DataAccessLayer';
import { ats } from '../../../DataAccessLayer/services';
import { Box, Button, Container, LinearProgress, Stack, Typography } from '@mui/material';
import PageHeader from '../../common/PageHeader';
import { PageDetails } from '../config';
import PortalDataGridPro from '../../common/PortalDataGridPro';
import { getColumns } from './columns';
import CodecModal from './CodecModal';
import Loader from '../../common/Loader';
import { toast } from 'react-toastify';
import PortalDelete from '../../common/PortalDelete';
import UploadForm from './UploadForm';
import AddUpdateForm from './AddUpdateForm';
import PortalAlert from '../../common/PortalAlert';
import ApiErrorMessage from '../../common/ApiErrorMessage';

export default function Codecs() {
  const id = 'codecs';
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [codecs, setCodecs] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedCodec, setSelectedCodec] = useState();
  const [isInProgress, setIsInProgress] = useState(false);

  // Retrieves page title from pageDetails using pageid
  useEffect(() => {
    const loadPageTitle = () => {
      const page = PageDetails[id];
      setTitle(page.title);
    };
    loadPageTitle();
  }, []);

  const toggleIsLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const toggleIsError = (value) => {
    setIsError(value);
  };

  const toggleIsInProgress = () => {
    setIsInProgress((prev) => !prev);
  };

  const toggleIsAddModal = () => {
    setIsAddModalOpen((prev) => !prev);
  };

  const toggleIsUploadModal = () => {
    setIsUploadModalOpen((prev) => !prev);
  };

  const toggleIsEditModal = () => {
    setIsEditModalOpen((prev) => !prev);
  };

  const toggleIsDeleteModal = () => {
    setIsDeleteModalOpen((prev) => !prev);
  };

  // Retrieves codecs list
  useEffect(() => {
    loadCodecs();
  }, []);

  // Loads codecs
  const loadCodecs = () => {
    toggleIsLoading(true);
    toggleIsError(false);
    getData(ats.codecs, true)
      .then((response) => {
        setCodecs(response);
      })
      .catch((err) => {
        console.log(err);
        toggleIsError(true);
      })
      .finally(() => {
        toggleIsLoading(false);
      });
  };

  // Adds codecs
  const onAdd = async (data) => {
    try {
      toggleIsInProgress();
      await postData(ats.codecs, data, true);
      toast.success('Codec added.');
      toggleIsAddModal();
      loadCodecs();
    } catch (err) {
      console.log(err);
      toast.error('Unable to add codec.');
    } finally {
      toggleIsInProgress();
    }
  };

  // Uploads codecs CSV
  const onUpload = async (file) => {
    try {
      toggleIsInProgress();
      const formData = new FormData();
      formData.append('file', file);
      await postData(ats.importCodecs, formData, true);
      toast.success('CSV uploaded.');
      loadCodecs();
      toggleIsUploadModal(false);
    } catch (err) {
      console.log(err);
      toast.error('Unable to upload the CSV.');
    } finally {
      toggleIsInProgress();
    }
  };

  const onEdit = (codec) => {
    setSelectedCodec(codec);
    toggleIsEditModal();
  };

  // Updates codecs
  const onEditSubmit = async (codec) => {
    try {
      toggleIsInProgress();
      await putData(ats.codecs, codec, true);
      toast.success('Codec updated.');
      toggleIsEditModal();
      toggleIsInProgress();
      loadCodecs();
      setSelectedCodec(null);
    } catch (err) {
      console.log(err);
      toast.error('Unable to update codec.');
      toggleIsInProgress();
    }
  };

  const onDelete = (codec) => {
    setSelectedCodec(codec);
    toggleIsDeleteModal();
  };

  // Deletes codecs
  const onDeleteSubmit = async () => {
    try {
      toggleIsInProgress();
      await deleteData(ats.deleteCodecs(selectedCodec.ID), {}, true);
      toast.success('Codec deleted.');
      toggleIsDeleteModal();
      toggleIsInProgress();
      loadCodecs();
      setSelectedCodec(null);
    } catch (err) {
      console.log(err);
      toast.error('Unable to delete codec.');
      toggleIsInProgress();
    }
  };

  return (
    <div id={`${id}__div_pageWrapper`} className={`myOdu__${id}`}>
      <Loader id={`${id}__loader`} isOpen={isInProgress} />

      <CodecModal
        id={`${id}Add`}
        isOpen={isAddModalOpen}
        title="Add Codec"
        toggleModalOpen={toggleIsAddModal}
      >
        <AddUpdateForm id={`${id}Add`} onAction={onAdd} />
      </CodecModal>

      {isDeleteModalOpen && selectedCodec && (
        <PortalDelete
          onToggle={toggleIsDeleteModal}
          onDelete={onDeleteSubmit}
          message={`Are you sure you want to delete ${selectedCodec.SYSTEM_NAME}?`}
          title={'Delete Codec'}
          id={`${id}__deleteCodecs_confirmation_modal`}
        />
      )}
      <CodecModal
        id={`${id}Edit`}
        isOpen={isEditModalOpen}
        title="Edit Codec"
        toggleModalOpen={toggleIsEditModal}
      >
        <AddUpdateForm
          id={`${id}Edit`}
          onAction={onEditSubmit}
          isEdit={true}
          selectedCodec={selectedCodec}
        />
      </CodecModal>

      <CodecModal
        id={`${id}UploadCsv`}
        isOpen={isUploadModalOpen}
        title="Upload Codecs CSV"
        toggleModalOpen={toggleIsUploadModal}
      >
        <UploadForm id={`${id}UploadCsv`} onUpload={onUpload} />
      </CodecModal>

      <Container id={`${id}__container_pageWrapper`} className="myOdu__container secondaryPage">
        <Stack
          id={`${id}__stack_wrapper`}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Stack id={`${id}__stack_pageHeaderWrapper`} direction="column">
            <PageHeader id={id} title={title} isSubPage pageConfig={PageDetails} />
          </Stack>
          <Box id={`${id}__box_buttonsWrapper`}>
            <Button
              id={`${id}__button_addCodec`}
              className="myOdu__button secondary"
              variant="outlined"
              onClick={toggleIsAddModal}
              sx={{ mr: 2 }}
            >
              Add Codec
            </Button>
            <Button
              id={`${id}__button_uploadCsv`}
              className="myOdu__button secondary"
              variant="outlined"
              onClick={toggleIsUploadModal}
            >
              Upload CSV
            </Button>
          </Box>
        </Stack>

        {!isLoading && isError && (
          <PortalAlert id={`${id}__portalAlert`} severity="error">
            <ApiErrorMessage
              id={`${id}__apiErrorMessage`}
              reload={loadCodecs}
              widgetName="Manage Codecs"
              isPage={true}
            />
          </PortalAlert>
        )}
        <Box id={`${id}__box_wrapper`} className={'wrapper'}>
          <PortalDataGridPro
            className="myOdu__resultTable"
            getRowHeight={() => 'auto'}
            id={`${id}__dataGrid`}
            columns={getColumns({ id, onEdit, onDelete })}
            sx={{
              '& .MuiDataGrid-cell--pinnedLeft': {
                zIndex: '10 !important',
              },
              '& .MuiDataGrid-cell': {
                whiteSpace: 'nowrap !important',
                minHeight: '3rem',
                alignContent: 'center',
              },
            }}
            rows={codecs}
            getRowId={(row) => row.SYSTEM_NAME + row.CODEC_ID}
            checkboxSelection={false}
            disableRowSelectionOnClick
            isLoading={isLoading}
            slots={{
              loadingOverlay: LinearProgress,
              noRowsOverlay: () => {
                return (
                  <Box
                    mt={1}
                    display="flex"
                    justifyContent="center"
                    id={`${id}__box_noResultsWrapper`}
                  >
                    <Typography id={`${id}__p_noResults`} variant="p" component="p">
                      No codecs found.
                    </Typography>
                  </Box>
                );
              },
            }}
            autoHeight
            initialState={{
              sorting: {
                sortModel: [
                  // Initially sort based on the column 'UPDATED_AT' and then 'CREATED_AT' in descending order
                  { field: 'UPDATED_AT', sort: 'desc' },
                  { field: 'CREATED_AT', sort: 'desc' },
                ],
              },
              pagination: {
                paginationModel: { pageSize: 20 },
              },
            }}
            pagination
          />
        </Box>
      </Container>
    </div>
  );
}
